import { lifetimeShareForRange } from "./constants/filters";
import { COPY } from "./copy";
import { pickWeightedStoreType } from "./insightsMockData";
import { getActiveProfileToken, getP8Profile } from "./p8AdvertiserData";
import {
  formatOverviewCount,
  formatOverviewCurrency,
  getP8AdPerformanceByMonthRows,
  sumConversions,
  type P8OverviewKpi,
} from "./p8OverviewData";
import type { AdsDateRange, InsightsCustomerSegment } from "./types";

export type CustomerType = "New" | "Returning";

export type AcquiredCustomerRow = {
  id: string;
  storeName: string;
  customerType: CustomerType;
  storeType: string;
  /** "City, ST". Modeled like the store name — a real pull can't be shown here. */
  location: string;
  /** ISO date the retailer clicked the ad. */
  adClickDate: string;
  /**
   * ISO date of the order the ad drove: the one that acquired them for a new
   * customer ad, or the reorder itself for a reorder ad. This is the only order
   * the table reports — a new customer's later orders aren't shown, so there is
   * deliberately no "last ordered" counterpart here.
   */
  firstOrdered: string;
  /** Dollars for the order that acquired / re-acquired them. */
  firstMonthAdSales: number;
  /**
   * Lifetime totals across every order the retailer has placed with this brand,
   * new and returning alike — for a returning customer that includes the orders
   * they placed before the ad, which ads can't take credit for. Nullable because
   * a real pull can come back without lifetime attribution; null → render "—".
   */
  lifetimeOrders: number | null;
  lifetimeSales: number | null;
};

/**
 * What a new customer bought in the 12 months after the order that acquired
 * them. The lifetime totals count that acquiring order, so the continued figures
 * are the remainder — the same cut the Overview's Continued ad sales column
 * makes, one customer at a time instead of one cohort at a time. Null carries
 * straight through from the lifetime totals, which a real pull can return
 * unattributed.
 */
export function getCustomerContinuedAdSales(
  row: AcquiredCustomerRow
): number | null {
  if (row.lifetimeSales == null) {
    return null;
  }
  return Math.max(0, row.lifetimeSales - row.firstMonthAdSales);
}

/**
 * Whether a row's forward-looking columns — continued ad sales, lifetime sales,
 * lifetime orders — say anything about the ad that drove it. A reorder ad buys a
 * single order and can't claim what surrounds it, so on those rows the columns
 * read as not-applicable rather than as figures the ad earned.
 *
 * Distinct from a null lifetime total, which means the pull came back without
 * attribution. Both render gray, but only one of them could later be a number.
 */
export function hasContinuedAttribution(row: AcquiredCustomerRow): boolean {
  return row.customerType === "New";
}

/**
 * The order the Customers table's Order date and Order amount columns report:
 * the single order the ad drove. For a new customer ad that's the order that
 * converted them, and for a reorder ad it's the reorder.
 *
 * One order either way. A new customer's subsequent orders are inside the 12
 * months Faire credits to the acquiring click, but the table doesn't show them —
 * their contribution surfaces in continued ad sales and the lifetime columns
 * instead. Kept as a named accessor rather than reading the fields directly, so
 * the columns can't drift apart from each other or from the tooltip.
 */
export function getAttributedOrder(row: AcquiredCustomerRow): {
  date: string;
  amount: number;
} {
  return { date: row.firstOrdered, amount: row.firstMonthAdSales };
}

/**
 * MODELED customer-level detail. Real retailer names can't be shown in a
 * prototype, so the acquired-customer rows below are synthesized
 * deterministically per profile: store names, locations, dates, and order
 * amounts are seeded, while each row's store type is drawn from the profile's
 * real store-type mix (see `pickWeightedStoreType`). The aggregate KPIs above the
 * table come from the profile overview and are real for the real-brand
 * variants. See snowflakeQueries.ts (COHORT_CUSTOMERS) for the refresh query.
 */

/** Modeled lifetime orders per new customer, for profiles with no real count. */
const NEW_CUSTOMER_LIFETIME_ORDER_RATE = 2.75;
/** Modeled lifetime orders per returning customer. */
const RETURNING_LIFETIME_ORDER_RATE = 1.9;

const STORE_NAME_POOL = [
  "Sharp Fits",
  "Future Trinket",
  "Sensispa",
  "Glowing Zen",
  "Bend Gift Shop",
  "Ross Museum",
  "Marley's",
  "Radical Notion Apparel LLC.",
  "Styled Sanctuary",
  "Wild Poppy Home",
  "The Nested Fig",
  "Coastal Mercantile",
  "Juniper & Sage",
  "Little Lark Toys",
  "Harbor & Pine",
  "Maison Vert",
  "The Copper Kettle",
  "Fern & Flora",
  "Golden Hour Goods",
  "Petal Pushers",
  "Stonecrop Studio",
  "The Salted Rose",
  "Blue Heron Boutique",
  "Wandering Wren",
  "Terra Cotta Co.",
  "Moss & Maple",
  "The Gilded Owl",
  "Sunday Supply Co.",
  "Rivertown Reads",
  "Alder & Oak",
  "Bluebird Bazaar",
  "The Velvet Antler",
  "Cedar & Salt",
  "Poppy Lane",
  "Hazel & Hollis",
  "Dune Home Goods",
  "The Paper Parlor",
  "Willowbrook Wares",
  "Ivy & Ember",
  "North Star Naturals",
  "Tumbleweed Trading",
  "Marigold Mercantile",
  "Saffron & Sage",
  "Bramble & Bee",
  "The Tin Rabbit",
  "Lark & Larder",
];

/**
 * Drawn independently of the store name, so the same shop doesn't always sit in
 * the same town when the profile reshuffles. Skewed toward the small
 * independent-retail towns that dominate wholesale, rather than major metros.
 */
const STORE_LOCATION_POOL = [
  "Portland, OR",
  "Asheville, NC",
  "Austin, TX",
  "Burlington, VT",
  "Santa Fe, NM",
  "Savannah, GA",
  "Boulder, CO",
  "Providence, RI",
  "Ann Arbor, MI",
  "Charleston, SC",
  "Madison, WI",
  "Missoula, MT",
  "Ithaca, NY",
  "Bozeman, MT",
  "Sarasota, FL",
  "Lawrence, KS",
  "Traverse City, MI",
  "Chattanooga, TN",
  "Bar Harbor, ME",
  "Taos, NM",
  "Hood River, OR",
  "Sturgeon Bay, WI",
  "Bisbee, AZ",
  "Hudson, NY",
];

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function seedRand(n: number): number {
  const x = Math.sin(n * 9301 + 49297) * 10000;
  return x - Math.floor(x);
}

/** Deterministic per-profile seed so switching advertisers reshuffles the list. */
function profileSeedOffset(): number {
  const token = getActiveProfileToken();
  let hash = 0;
  for (let i = 0; i < token.length; i += 1) {
    hash = (hash * 31 + token.charCodeAt(i)) % 100000;
  }
  return hash;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function isoDate(year: number, month1: number, day: number): string {
  return `${year}-${pad2(month1)}-${pad2(day)}`;
}

export function formatCustomerDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

export function formatCustomerCurrency(amount: number): string {
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function monthFromIndex(monthIndex: number): { year: number; month1: number } {
  return {
    year: Math.floor(monthIndex / 12),
    month1: (monthIndex % 12) + 1,
  };
}

export function monthLabelFromIndex(monthIndex: number): string {
  const { year, month1 } = monthFromIndex(monthIndex);
  return `${MONTHS_SHORT[month1 - 1]} ${year}`;
}

function daysInMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

type RowOptions = {
  /** Constrain the ad-click / first-order dates to this acquisition month. */
  acquisitionMonthIndex?: number;
  /** Force the customer type instead of seeding it. */
  forceType?: CustomerType;
};

function buildRow(seed: number, options: RowOptions = {}): AcquiredCustomerRow {
  const r1 = seedRand(seed + 1);
  const r2 = seedRand(seed + 2);
  const r3 = seedRand(seed + 3);
  const r4 = seedRand(seed + 4);
  const r5 = seedRand(seed + 5);
  const r6 = seedRand(seed + 6);

  const storeName = STORE_NAME_POOL[seed % STORE_NAME_POOL.length];
  const storeType = pickWeightedStoreType(r1);
  const location =
    STORE_LOCATION_POOL[Math.floor(r6 * STORE_LOCATION_POOL.length)];
  const customerType: CustomerType =
    options.forceType ?? (r2 < 0.68 ? "New" : "Returning");

  const acquisitionMonthIndex =
    options.acquisitionMonthIndex ??
    getCurrentMonthIndexApprox() - 1 - Math.floor(r3 * 14);
  const { year, month1 } = monthFromIndex(acquisitionMonthIndex);
  const dim = daysInMonth(year, month1);
  const clickDay = 1 + Math.floor(r3 * (dim - 2));
  const firstOrderDay = Math.min(dim, clickDay + 1 + Math.floor(r4 * 3));

  const adClickDate = isoDate(year, month1, clickDay);
  const firstOrdered = isoDate(year, month1, firstOrderDay);

  const firstMonthAdSales = Math.round((60 + r1 * 320) * 100) / 100;

  const adAttributedOrders = 1 + Math.floor(r2 * 4);
  // A returning customer had already bought from this brand before the ad, so
  // their lifetime spans orders the ads didn't drive. Those sit on top of the
  // ad-attributed ones rather than replacing them, which is why returning rows
  // read higher here than new ones do.
  const priorOrders = customerType === "New" ? 0 : 1 + Math.floor(r5 * 3);
  const lifetimeOrders = adAttributedOrders + priorOrders;
  // A retailer with a single order to their name has bought exactly the order
  // the ad drove, so their lifetime total is that order rather than a multiple
  // of it. Without this the row contradicts itself in two adjacent columns: one
  // lifetime order, beside a lifetime figure larger than the only order in it.
  const lifetimeSales =
    lifetimeOrders === 1
      ? firstMonthAdSales
      : Math.round(
          firstMonthAdSales * lifetimeOrders * (0.9 + r4 * 0.4) * 100
        ) / 100;

  return {
    id: `cust-${seed}`,
    storeName,
    customerType,
    storeType,
    location,
    adClickDate,
    firstOrdered,
    firstMonthAdSales,
    lifetimeOrders,
    lifetimeSales,
  };
}

/** Approximate "now" for date generation without importing overview periods. */
function getCurrentMonthIndexApprox(): number {
  const now = new Date(2026, 6, 1); // Jul 2026 anchor, matches overview
  return now.getFullYear() * 12 + now.getMonth();
}

function getCustomerCounts() {
  const overview = getP8Profile().overview;
  // Mirror the Overview "Conversions" KPI so the Customers tab totals reconcile:
  // new + returning customers === the sum of conversions across the month table.
  const conversions = sumConversions(getP8AdPerformanceByMonthRows());
  const newCustomers = conversions.new;
  const returningCustomers = conversions.returning;
  // Real ad-attributed order counts where we have them; the modeled rates only
  // stand in for profiles that never had a Snowflake pull.
  const newOrders =
    overview.numNewCustomerAdsOrders ??
    Math.round(newCustomers * NEW_CUSTOMER_LIFETIME_ORDER_RATE);
  const returningOrders =
    overview.numReorderAdsOrders ??
    Math.round(returningCustomers * RETURNING_LIFETIME_ORDER_RATE);
  const newSales = overview.newCustomerAdsSalesDollars ?? 0;
  const newSpend = overview.newCustomerAdsSpendDollars ?? 0;
  // The new-only archetypes report just the new-customer share, so the reorder
  // side falls back to the residual against the program total. That keeps the
  // two segments summing to the totals the Overview tab shows.
  return {
    newCustomers,
    returningCustomers,
    newOrders,
    returningOrders,
    newSales,
    returningSales:
      overview.reorderAdsSalesDollars ??
      Math.max(0, overview.totalAdsSalesDollars - newSales),
    newSpend,
    returningSpend:
      overview.reorderAdsSpendDollars ??
      Math.max(0, overview.totalAdsSpendDollars - newSpend),
    totalSales: overview.totalAdsSalesDollars,
    totalSpend: overview.totalAdsSpendDollars,
  };
}

/**
 * The program totals are lifetime figures, so a shorter range takes a share of
 * them. Counts are rounded so the cards keep reporting whole customers and
 * orders; the two averages built from them are ratios and so survive the scale
 * either way.
 */
function scaleCounts(
  counts: ReturnType<typeof getCustomerCounts>,
  dateRange: AdsDateRange
): ReturnType<typeof getCustomerCounts> {
  const share = lifetimeShareForRange(dateRange);
  if (share === 1) {
    return counts;
  }
  return {
    newCustomers: Math.round(counts.newCustomers * share),
    returningCustomers: Math.round(counts.returningCustomers * share),
    newOrders: Math.round(counts.newOrders * share),
    returningOrders: Math.round(counts.returningOrders * share),
    newSales: counts.newSales * share,
    returningSales: counts.returningSales * share,
    newSpend: counts.newSpend * share,
    returningSpend: counts.returningSpend * share,
    totalSales: counts.totalSales * share,
    totalSpend: counts.totalSpend * share,
  };
}

/** The four figures every headline metric on this tab is derived from. */
type SegmentFacts = {
  customers: number;
  orders: number;
  sales: number;
  spend: number;
};

/**
 * "All" reads the profile's own program totals rather than adding the two
 * segments together: the reported totals are authoritative, and the segment
 * figures can disagree with them by a dollar or two of rounding.
 */
function factsForSegment(
  counts: ReturnType<typeof getCustomerCounts>,
  segment: InsightsCustomerSegment
): SegmentFacts {
  if (segment === "new-customers") {
    return {
      customers: counts.newCustomers,
      orders: counts.newOrders,
      sales: counts.newSales,
      spend: counts.newSpend,
    };
  }
  if (segment === "returning-customers") {
    return {
      customers: counts.returningCustomers,
      orders: counts.returningOrders,
      sales: counts.returningSales,
      spend: counts.returningSpend,
    };
  }
  return {
    customers: counts.newCustomers + counts.returningCustomers,
    orders: counts.newOrders + counts.returningOrders,
    sales: counts.totalSales,
    spend: counts.totalSpend,
  };
}

/** A single ad type, i.e. the states where the per-conversion averages appear. */
type FilteredSegment = Exclude<InsightsCustomerSegment, "all-customers">;

/**
 * What one conversion is, for the footers that report an average's denominator.
 * A new customer ad's conversion is a customer; a reorder ad's is an order.
 */
const CONVERSION_NOUN: Record<FilteredSegment, string> = {
  "new-customers": "new customers",
  "returning-customers": "reorders",
};

/** What the spend was spent on — the ads, not the customers they brought in. */
const AD_TYPE_NOUN: Record<FilteredSegment, string> = {
  "new-customers": "new customer ads",
  "returning-customers": "reorder ads",
};

/**
 * The titles don't name the ad type. The filter above the cards already says
 * which ads these are, so spelling it out in five titles only makes each one
 * longer to read; what the ad type changes is which cards appear at all, and
 * what each tooltip says a conversion is.
 *
 * No range words either — a title has to read correctly under any date range the
 * tab is set to, so the period is the filter's job to state, not the card's.
 */
const TITLES = {
  conversions: "Conversions",
  orders: "Orders",
  avgOrders: "Average orders",
  avgValue: "Average conversion value",
  avgCost: "Average cost per conversion",
} as const;

const CONVERSIONS_TOOLTIPS: Record<InsightsCustomerSegment, string> = {
  "all-customers": COPY.customers.kpi.conversions.all,
  "new-customers": COPY.customers.kpi.conversions.new,
  "returning-customers": COPY.customers.kpi.conversions.returning,
};

const ORDERS_TOOLTIPS = {
  "all-customers": COPY.customers.kpi.orders.all,
  "new-customers": COPY.customers.kpi.orders.new,
} as const;

const AVG_ORDERS_TOOLTIP = COPY.customers.kpi.avgOrders;

const AVG_VALUE_TOOLTIPS: Record<FilteredSegment, string> = {
  "new-customers": COPY.customers.kpi.avgValue.new,
  "returning-customers": COPY.customers.kpi.avgValue.returning,
};

const AVG_COST_TOOLTIPS: Record<FilteredSegment, string> = {
  "new-customers": COPY.customers.kpi.avgCost.new,
  "returning-customers": COPY.customers.kpi.avgCost.returning,
};

/** A segment with no customers has no average to show, so it renders "—". */
function perCustomer(numerator: number, customers: number): number | null {
  return customers > 0 ? numerator / customers : null;
}

function formatShareFooter(
  part: number,
  whole: number,
  noun: string
): string[] {
  if (whole <= 0) {
    return [];
  }
  return [`${Math.round((part / whole) * 100)}% of all ${noun}`];
}

export function getCustomerKpis(
  segment: InsightsCustomerSegment,
  dateRange: AdsDateRange
): P8OverviewKpi[] {
  const counts = scaleCounts(getCustomerCounts(), dateRange);
  const facts = factsForSegment(counts, segment);

  const totalCustomers = counts.newCustomers + counts.returningCustomers;
  const totalOrders = counts.newOrders + counts.returningOrders;

  const conversions: P8OverviewKpi = {
    title: TITLES.conversions,
    value: formatOverviewCount(facts.customers),
    tooltip: CONVERSIONS_TOOLTIPS[segment],
    footers:
      segment === "all-customers"
        ? [
            `${formatOverviewCount(counts.newCustomers)} from new customer ads`,
            `${formatOverviewCount(counts.returningCustomers)} from reorder ads`,
          ]
        : formatShareFooter(facts.customers, totalCustomers, "conversions"),
  };

  const ordersKpi = (tooltip: string): P8OverviewKpi => ({
    title: TITLES.orders,
    value: formatOverviewCount(facts.orders),
    tooltip,
    footers:
      segment === "all-customers"
        ? [
            `${formatOverviewCount(counts.newOrders)} from new customer ads`,
            `${formatOverviewCount(counts.returningOrders)} from reorder ads`,
          ]
        : formatShareFooter(facts.orders, totalOrders, "orders"),
  });

  // An average across both ad types is a blend of two populations that behave
  // nothing alike — a first order and a reorder — so the combined figure
  // describes neither. The averages appear once an ad type is chosen and the
  // denominator means something.
  if (segment === "all-customers") {
    return [conversions, ordersKpi(ORDERS_TOOLTIPS["all-customers"])];
  }

  const noun = CONVERSION_NOUN[segment];
  const avgValue = perCustomer(facts.sales, facts.customers);
  const avgCost = perCustomer(facts.spend, facts.customers);

  // Cost last so it sits next to the value it's worth reading against.
  const perConversion: P8OverviewKpi[] = [
    {
      title: TITLES.avgValue,
      value: avgValue == null ? "—" : formatOverviewCurrency(avgValue),
      tooltip: AVG_VALUE_TOOLTIPS[segment],
      footers: [
        `${formatOverviewCurrency(facts.sales)} from ${noun}`,
        `${formatOverviewCount(facts.customers)} ${noun}`,
      ],
    },
    {
      title: TITLES.avgCost,
      value: avgCost == null ? "—" : formatOverviewCurrency(avgCost),
      tooltip: AVG_COST_TOOLTIPS[segment],
      footers: [
        // Named for the ad type, since a filtered card is showing that type's
        // slice of spend rather than the whole program's.
        `${formatOverviewCurrency(facts.spend)} spent on ${AD_TYPE_NOUN[segment]}`,
        `${formatOverviewCount(facts.customers)} ${noun}`,
      ],
    },
  ];

  // A reorder ad's conversion is itself an order, so an order count here would
  // only restate Conversions and the average would always be 1.00. Both drop out
  // rather than sitting on the page as figures that can't say anything.
  if (segment === "returning-customers") {
    return [conversions, ...perConversion];
  }

  const avgOrders = perCustomer(facts.orders, facts.customers);
  return [
    conversions,
    ordersKpi(ORDERS_TOOLTIPS["new-customers"]),
    {
      title: TITLES.avgOrders,
      value: avgOrders == null ? "—" : avgOrders.toFixed(2),
      tooltip: AVG_ORDERS_TOOLTIP,
      footers: [
        `${formatOverviewCount(facts.orders)} orders`,
        `${formatOverviewCount(facts.customers)} ${noun}`,
      ],
    },
    ...perConversion,
  ];
}

/** Full acquired-customer list for the Customers tab (across recent months). */
export function getAcquiredCustomerRows(): AcquiredCustomerRow[] {
  const offset = profileSeedOffset();
  return Array.from({ length: STORE_NAME_POOL.length }, (_, index) =>
    buildRow(offset + index)
  );
}

/**
 * Customers acquired / re-acquired in a specific month (drilldown cohort).
 *
 * The row count matches that month's Conversions KPI exactly (new + returning),
 * so the table can't show more conversions than the header reports. New and
 * returning rows are generated to match the KPI's segment footers too.
 */
export function getMonthCohortRows(monthIndex: number): AcquiredCustomerRow[] {
  const offset = profileSeedOffset();
  const monthRow = getP8AdPerformanceByMonthRows().find(
    (row) => row.monthIndex === monthIndex
  );
  const newCount = monthRow?.newConversionsCount ?? 0;
  const returningCount = monthRow?.returningConversionsCount ?? 0;
  const base = offset + monthIndex * 7;

  const newRows = Array.from({ length: newCount }, (_, index) =>
    buildRow(base + index, {
      acquisitionMonthIndex: monthIndex,
      forceType: "New",
    })
  );
  const returningRows = Array.from({ length: returningCount }, (_, index) =>
    buildRow(base + newCount + index, {
      acquisitionMonthIndex: monthIndex,
      forceType: "Returning",
    })
  );
  return [...newRows, ...returningRows];
}
